import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { errMsg } from "./api";

export const looksLikeKimkimRef = (s) => /activities-transportation\/\d+/.test(s || "") || /^\s*\d{3,}\s*$/.test(s || "");

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[’'`´]/g, "").toLowerCase();
// Mini diccionario ES/PT → EN: los tarifarios vienen en español y los títulos de KimKim en inglés
const SYN = { cocina: "cooking", cozinha: "cooking", clase: "class", taller: "workshop", mercado: "market", visita: "visit", paseo: "walk", caminata: "walk", ruta: "route",
  cata: "tasting", degustacion: "tasting", vino: "wine", vinos: "wine", bodega: "winery", aceite: "oil", tapas: "tapas", flamenco: "flamenco", bici: "bike", bicicleta: "bike",
  barco: "boat", velero: "sailing", vela: "sailing", kayak: "kayak", senderismo: "hiking", montana: "mountain", playa: "beach", isla: "island", ciudad: "city", pueblo: "village",
  catedral: "cathedral", palacio: "palace", castillo: "castle", museo: "museum", jardin: "garden", cueva: "cave", atardecer: "sunset", noche: "night", privado: "private", privada: "private",
  grupo: "group", familiar: "family", ninos: "kids", medio: "half", completo: "full", dia: "day", traslado: "transfer", aeropuerto: "airport", tren: "train", caballo: "horse", arte: "art",
  andaluz: "andalusian", andaluza: "andalusian", comida: "food", gastronomia: "food", sevilla: "seville", lisboa: "lisbon", oporto: "porto", cordoba: "cordoba", malaga: "malaga", cadiz: "cadiz", mallorca: "majorca" };
// Todas las palabras buscadas deben aparecer (en cualquier orden), sin acentos ni apóstrofes; cada palabra vale también por su equivalente inglés: "cocina sevilla" ✓
export const matchesWords = (value, search) => {
  const words = norm(search).split(/\s+/).filter(Boolean);
  const v = norm(value);
  return words.every((w) => v.includes(w) || (SYN[w] && v.includes(SYN[w]))) ? 1 : 0;
};

export function ActivityPicker({ activities, value, onChange, testId, onLookup }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [looking, setLooking] = useState(false);
  const current = activities.find((a) => a.id === value);
  const label = (a) => `${a.title}${a.location ? ` · ${a.location}` : ""}${a.operator ? ` · ${a.operator}` : ""} ${a.pricing_type ? `(${a.pricing_type === "group_price" ? "grupo" : "por persona"})` : "(sin precios)"}${a.owner === "kimkim" ? " · PROPIEDAD DE KIMKIM" : ""}`;

  const lookup = async () => {
    setLooking(true);
    try {
      const a = await onLookup(search.trim());
      if (a.owner === "kimkim") toast.warning(`«${a.title}» es propiedad de Kimkim: no se actualizará`);
      else toast.success(`Ficha ${a.id} «${a.title}» leída de KimKim${a.in_catalog ? "" : " (no estaba en el catálogo sincronizado)"}`);
      onChange(a.id);
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo leer la ficha en KimKim"));
    } finally {
      setLooking(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid={testId} variant="outline" role="combobox" aria-expanded={open} size="sm" className="h-8 rounded-md text-xs w-full md:w-[28rem] justify-between font-normal">
          <span className="truncate">{current ? label(current) : "Buscar actividad de KimKim…"}</span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[32rem] p-0 rounded-md" align="start">
        <Command filter={matchesWords}>
          <CommandInput data-testid={`${testId}-search`} value={search} onValueChange={setSearch} placeholder="Palabras del título, lugar, operador… o pega el ID / URL de KimKim" className="text-xs" />
          <CommandList className="max-h-72">
            <CommandEmpty>
              <p>Ninguna actividad del catálogo sincronizado contiene todas esas palabras.</p>
              <p className="text-muted-foreground mt-1">Prueba con menos palabras (p. ej. solo «Segovia») o pega la URL / ID de la ficha de KimKim.</p>
              {onLookup && looksLikeKimkimRef(search) && (
                <Button size="sm" variant="outline" className="rounded-md mt-2 text-xs" onClick={lookup} disabled={looking} data-testid={`${testId}-lookup`}>
                  {looking ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />} Leer esta ficha en KimKim
                </Button>
              )}
            </CommandEmpty>
            <CommandGroup>
              {activities.map((a) => (
                <CommandItem key={a.id} value={`${a.title} ${a.location || ""} ${a.operator || ""} ${a.id}`} onSelect={() => { onChange(a.id === value ? null : a.id); setOpen(false); }} className="text-xs" data-testid={`${testId}-option-${a.id}`}>
                  <Check className={`mr-2 h-3 w-3 ${value === a.id ? "opacity-100" : "opacity-0"}`} />
                  <span className={`truncate ${a.owner === "kimkim" ? "text-red-700" : ""}`}>{label(a)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
